import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Sync Store
 * مخزن Zustand لإدارة حالة المزامنة
 *
 * Features:
 * - Track online/offline status
 * - Store last sync timestamp
 * - Track pending sync items
 * - Sync queue management
 *
 * Protocols Applied:
 * - P-STATE-001: Zustand State Management
 * - P-OFFLINE-001: Offline-First Strategy
 * - P-DATA-001: Clear Data Structure
 */
const useSyncStore = create(
  persist(
    (set, get) => ({
      // ==================== STATE ====================

      lastSyncTime: null,
      isSyncing: false,
      syncQueue: [], // Queue of items to sync
      syncErrors: [],

      // ==================== ACTIONS ====================

      /**
       * Set last sync time
       */
      setLastSyncTime: (timestamp = new Date().toISOString()) => {
        set({ lastSyncTime: timestamp })
        console.log('[SyncStore] Last sync time updated:', timestamp)
      },

      /**
       * Set syncing status
       */
      setSyncing: (status) => {
        set({ isSyncing: status })
        console.log('[SyncStore] Syncing status:', status)
      },

      /**
       * Add item to sync queue
       * @param {Object} item - Item to sync (invoice, payment, etc.)
       */
      addToSyncQueue: (item) => {
        const queue = get().syncQueue
        const existingIndex = queue.findIndex(q => q.id === item.id && q.type === item.type)

        if (existingIndex >= 0) {
          // Update existing item
          const newQueue = [...queue]
          newQueue[existingIndex] = { ...item, updatedAt: new Date().toISOString() }
          set({ syncQueue: newQueue })
          console.log('[SyncStore] Updated item in sync queue:', item.type, item.id)
        } else {
          // Add new item
          set({
            syncQueue: [...queue, {
              ...item,
              addedAt: new Date().toISOString()
            }]
          })
          console.log('[SyncStore] Added to sync queue:', item.type, item.id)
        }
      },

      /**
       * Remove item from sync queue
       */
      removeFromSyncQueue: (itemId, itemType) => {
        set({
          syncQueue: get().syncQueue.filter(
            item => !(item.id === itemId && item.type === itemType)
          )
        })
        console.log('[SyncStore] Removed from sync queue:', itemType, itemId)
      },

      /**
       * Clear sync queue
       */
      clearSyncQueue: () => {
        set({ syncQueue: [] })
        console.log('[SyncStore] Sync queue cleared')
      },

      /**
       * Get sync queue count
       */
      getSyncQueueCount: () => {
        return get().syncQueue.length
      },

      /**
       * Add sync error
       */
      addSyncError: (error) => {
        set({
          syncErrors: [...get().syncErrors, {
            ...error,
            timestamp: new Date().toISOString()
          }]
        })
        console.error('[SyncStore] Sync error added:', error)
      },

      /**
       * Clear sync errors
       */
      clearSyncErrors: () => {
        set({ syncErrors: [] })
        console.log('[SyncStore] Sync errors cleared')
      },

      /**
       * Perform full sync
       * Syncs all pending items in the queue AND fetches latest data from API
       */
      performSync: async () => {
        const { syncQueue, setSyncing, setLastSyncTime, addSyncError } = get()

        if (!navigator.onLine) {
          console.log('[SyncStore] Cannot sync - offline')
          return { success: false, error: 'غير متصل بالإنترنت' }
        }

        setSyncing(true)

        try {
          console.log('[SyncStore] Starting full sync...')

          // Import stores dynamically
          const { default: useCustomerStore } = await import('./useCustomerStore')
          const { default: useProductStore } = await import('./useProductStore')

          // 1. Fetch latest data from API
          console.log('[SyncStore] Fetching latest data from API...')
          await Promise.all([
            useCustomerStore.getState().fetchCustomers(),
            useProductStore.getState().fetchMyVehicleInventory(), // Fixed: Sync vehicle inventory instead of all products
          ])

          // 2. Sync pending local changes (if any in queue)
          if (syncQueue.length > 0) {
            console.log('[SyncStore] Syncing', syncQueue.length, 'pending items...')
            // TODO: Implement actual API sync logic for queue items
            // For now, just clear the queue after fetching latest data
            set({ syncQueue: [] })
          }

          setLastSyncTime()
          setSyncing(false)

          console.log('[SyncStore] Sync completed successfully')
          return {
            success: true,
            message: syncQueue.length > 0
              ? `تمت مزامنة ${syncQueue.length} عنصر وتحديث البيانات بنجاح`
              : 'تم تحديث البيانات بنجاح'
          }

        } catch (error) {
          console.error('[SyncStore] Sync failed:', error)
          addSyncError({
            message: error.message || 'فشلت عملية المزامنة',
            items: syncQueue.length
          })
          setSyncing(false)

          return {
            success: false,
            error: 'فشلت عملية المزامنة. حاول مرة أخرى.'
          }
        }
      },

      /**
       * Check if device is online
       */
      isOnline: () => {
        return navigator.onLine
      }
    }),
    {
      name: 'sync-storage',
      // Don't persist syncing status
      partialize: (state) => ({
        lastSyncTime: state.lastSyncTime,
        syncQueue: state.syncQueue,
        syncErrors: state.syncErrors
      })
    }
  )
)

export default useSyncStore
